import { memo } from "react";
import { isEmpty } from "lodash";

import {
  AccountCashPanel,
  BrokerageTransferPanel,
  HoldingPanel,
  HoldingTradePanel,
  InvestmentPanel,
  PriceOverridePanel,
  ProgressPanel,
  SourceCountryPanel,
  TradeCorrectionPanel,
} from "../../panels";
import { InvestmentPanelsProps } from "./types";

// Decides which side panels are mounted and wires each to its command. It holds
// no state of its own — the page owns every controller.
const InvestmentPanelsComponent: React.FC<InvestmentPanelsProps> = ({
  investmentId,
  investment,
  isLedger,
  isManual,
  settlementAccountName,
  holdings,
  priceOverrideTarget,
  controllers,
  commands,
  investmentEdit,
  holdingEntry,
}) => {
  const {
    trade,
    accountCash,
    brokerageTransfer,
    correction,
    priceOverride,
    sourceCountry,
    progress,
  } = controllers;

  return (
    <>
      {!isEmpty(investmentEdit.investment) && (
        <InvestmentPanel
          investment={investmentEdit.investment}
          handleOnSubmit={commands.updateInvestment}
          isOpen={investmentEdit.isOpen}
          onClose={investmentEdit.onClose}
          trackingMode={investment.trackingMode}
          hasLedgerCommands={investment.hasLedgerCommands}
        />
      )}

      {isLedger && holdingEntry.isOpen && (
        <HoldingPanel
          isOpen={holdingEntry.isOpen}
          onClose={holdingEntry.onClose}
          investmentName={investment.name}
          investmentStartDate={investment.startDate}
          investmentAccountId={investment.accountId}
          settlementAccountName={settlementAccountName}
          handleOnSubmit={commands.buyHolding}
        />
      )}

      {/* Sell / dividend / fee panel. Handed the *full* holdings array — the
          query already runs with `includeClosed: true`, and a dividend against
          a since-sold position is a case the table's open-only view hides. */}
      {isLedger && trade.panel.isOpen && (
        <HoldingTradePanel
          isOpen={trade.panel.isOpen}
          onClose={trade.closePanel}
          investmentName={investment.name}
          investmentStartDate={investment.startDate}
          holdings={holdings}
          initialAction={trade.panel.action}
          initialHolding={trade.panel.holding}
          settlementAccountName={settlementAccountName}
          handleOnSubmit={commands.recordHoldingTrade}
        />
      )}

      {/* Account-level cash names no position, so it lives in the header overflow rather than the trade panel. */}
      {isLedger && accountCash.panel.isOpen && (
        <AccountCashPanel
          isOpen={accountCash.panel.isOpen}
          onClose={accountCash.closePanel}
          investmentName={investment.name}
          investmentStartDate={investment.startDate}
          settlementAccountName={settlementAccountName}
          handleOnSubmit={commands.recordAccountCash}
        />
      )}

      {isLedger && brokerageTransfer.panel.isOpen && (
        <BrokerageTransferPanel
          isOpen={brokerageTransfer.panel.isOpen}
          onClose={brokerageTransfer.closePanel}
          mode={brokerageTransfer.panel.mode}
          initialValues={brokerageTransfer.panel.initialValues}
          investmentName={investment.name}
          investmentAccountId={investment.accountId}
          investmentStartDate={investment.startDate}
          handleOnSubmit={commands.recordBrokerageTransfer}
        />
      )}

      {/* Trade-correction panel. Mounted only while open, so each correction
          starts from the clicked command's own defaults rather than from the
          previous one's. */}
      {isLedger && correction.panel.isOpen && correction.panel.command && (
        <TradeCorrectionPanel
          isOpen={correction.panel.isOpen}
          onClose={correction.closePanel}
          command={correction.panel.command}
          investmentName={investment.name}
          investmentStartDate={investment.startDate}
          investmentAccountId={investment.accountId}
          settlementAccountName={settlementAccountName}
          handleOnSubmit={commands.editTradeCommand}
        />
      )}

      {isLedger && priceOverride.panel.isOpen && (
        <PriceOverridePanel
          isOpen={priceOverride.panel.isOpen}
          onClose={priceOverride.closePanel}
          initialInstrument={priceOverrideTarget.initialInstrument}
          instruments={priceOverrideTarget.instruments}
        />
      )}

      {isLedger &&
        sourceCountry.panel.isOpen &&
        sourceCountry.panel.holding && (
          <SourceCountryPanel
            isOpen={sourceCountry.panel.isOpen}
            onClose={sourceCountry.closePanel}
            instrument={sourceCountry.panel.holding.instrument}
          />
        )}

      {isManual && progress.panel.isOpen && (
        <ProgressPanel
          title={progress.getPanelTitle()}
          progress={progress.panel.progress}
          account={{
            value: String(investment.accountId),
            label: investment.name,
          }}
          investmentId={investmentId}
          handleOnSubmit={commands.submitProgress}
          isOpen={progress.panel.isOpen}
          onClose={progress.closePanel}
        />
      )}
    </>
  );
};

// Memoised: the page re-renders on state no panel reads, and every prop object
// it passes is built with `useMemo` so this comparison can actually succeed.
export const InvestmentPanels = memo(InvestmentPanelsComponent);
